import { ReactNode } from 'react'

import type { AutomationsPresentationV1Node } from 'Automations/automationEditor/types'
import { ConditionalProperties } from 'Automations/nodeDefinitions/conditions'
import {
  isCustomPropertyOperatorId,
  readCustomPropertyValue
} from 'Automations/nodeDefinitions/conditions/helpers/functionStrings'
import { Condition } from '../..'

export type ConditionTextParts = {
  propertyLabel: ReactNode
  operatorLabel: ReactNode
  /**
   * The formatted condition value. Operators may return rich content
   * (tooltips, badges) via their `formatValue` callback so the Switch editor
   * node can render it as React. String-only consumers (e.g. execution summary
   * group labels) coerce via String().
   */
  valueLabel: ReactNode
}

export const formatConditionText = (
  condition: Condition,
  properties: ConditionalProperties,
  nodes: AutomationsPresentationV1Node[] = []
): ConditionTextParts | null => {
  const { property, operator, value } = condition

  if (!property || !operator) {
    return null
  }

  const propertyDef = properties[property]
  if (!propertyDef) return null

  const operatorDef = propertyDef.operators[operator]
  if (!operatorDef) return null

  const requiresValue = operatorDef.requiresValue !== false

  // A custom-property operator stores `{ operator, value }`; treat it as empty
  // until the comparand value is filled, so a half-configured row doesn't
  // render `equals ""` on the canvas.
  const valueEmpty = isCustomPropertyOperatorId(operator)
    ? isBlank(readCustomPropertyValue(value).value)
    : isValueEmpty(value)

  if (requiresValue && valueEmpty) {
    return null
  }

  return {
    propertyLabel: propertyDef.formatPreviewProperty
      ? propertyDef.formatPreviewProperty({ value, operator, nodes })
      : propertyDef.label,
    operatorLabel: operatorDef.formatPreviewOperator
      ? operatorDef.formatPreviewOperator({ operator, value })
      : operatorDef.label.toLowerCase(),
    valueLabel: requiresValue
      ? (operatorDef.formatPreviewValue?.({ value }) ?? String(value))
      : ''
  }
}

// Operators are free to store any shape on `value` (primitive, array, or
// `{ amount, unit }` for amount-with-unit). This predicate covers each shape.
// For amount-with-unit values both fields must be set — a missing unit means
// the user hasn't finished filling out the row, so we treat it as empty and
// hide the partial label from the canvas. The product-discount value
// extends this with `productId` and `operator` fields.
const isValueEmpty = (value: unknown): boolean => {
  if (value === undefined || value === null || value === '') return true
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'object' && value !== null && 'amount' in value) {
    const { amount, unit, productId, operator } = value as {
      amount: unknown
      unit?: unknown
      productId?: unknown
      operator?: unknown
    }
    const productIdMissing = 'productId' in value && isBlank(productId)
    const operatorMissing = 'operator' in value && isBlank(operator)
    return (
      isBlank(amount) || isBlank(unit) || productIdMissing || operatorMissing
    )
  }
  return false
}

const isBlank = (v: unknown): boolean =>
  v === undefined || v === null || v === ''
